import { useState } from "react";
import { Download, Scissors, RefreshCw, Type, Smartphone } from "lucide-react";
import { StatusPill } from "./shared";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAP = [["0:00–0:08", "Clip 1"], ["0:08–0:16", "Clip 2 · S1→S2→S3"], ["0:16–0:24", "Clip 3 · S4→S5"], ["0:24–0:27", "Clip 4 · S6"], ["0:27–0:30", "End card"]];

function Toggle({ on, onChange, icon: Icon, label, testId }) {
  return (
    <button type="button" data-testid={testId} aria-pressed={on} onClick={() => onChange(!on)}
      style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer", border: `1px solid ${on ? "var(--charcoal)" : "var(--ink-12)"}`, background: on ? "var(--charcoal)" : "#fff", color: on ? "var(--porcelain)" : "var(--ink-60)", transition: "background-color 160ms ease, color 160ms ease" }}>
      <Icon size={13} />{label}{on ? " · on" : " · off"}
    </button>
  );
}

export default function RoughCut({ cut, onBuild, spaStyle }) {
  const [titles, setTitles] = useState(true);
  const [insets, setInsets] = useState(true);
  if (!cut) return null;
  const busy = cut.status === "generating";
  return (
    <div className="card fade-up" data-testid="rough-cut" style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 32, padding: 28, marginTop: 56 }}>
      <div style={{ width: 240, aspectRatio: "9 / 16", borderRadius: 14, background: "var(--charcoal)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {cut.url ? (
          <video data-testid="rough-cut-player" src={`${BACKEND_URL}${cut.url}?t=${cut.created_at}`} poster={`${BACKEND_URL}/api/files/clip1_frame.png`} controls loop muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover", opacity: busy ? .5 : 1 }} />
        ) : (
          <span className={busy ? "pulse-dot serif" : "serif"} style={{ color: "var(--porcelain)", fontStyle: "italic", fontSize: 20 }}>{busy ? "stitching…" : "no cut yet"}</span>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="eyebrow">Rough cut · 30.00s · 1080×1920 · 24fps</div>
          <StatusPill status={cut.status} />
        </div>
        <h3 className="serif" style={{ fontSize: 34, fontWeight: 500, margin: "6px 0 0", lineHeight: 1.1 }}>One-click preview assembly</h3>
        <p style={{ fontSize: 14, lineHeight: 1.65, color: "var(--ink-60)", margin: "12px 0 0", maxWidth: 560 }}>
          Joins the four clips and the end card on the handoff timing map, burns in the three headline titles at their cue points, and drops the S1–S6 stills roughly onto the blank phone and tablet so the UI timing reads at a glance. Where a Kling MP4 isn't rendered yet, the key frame is animated with a slow push-in. Spa style: <b style={{ color: "var(--charcoal)" }}>{spaStyle}</b>.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
          {MAP.map(([t, l]) => <span key={t} style={{ fontSize: 11, fontWeight: 600, background: "var(--porcelain)", border: "1px solid var(--ink-06)", borderRadius: 999, padding: "5px 10px" }}><b style={{ color: "var(--burgundy)" }}>{t}</b> {l}</span>)}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <Toggle on={titles} onChange={setTitles} icon={Type} label="Burn-in titles" testId="toggle-titles" />
          <Toggle on={insets} onChange={setInsets} icon={Smartphone} label="UI insets" testId="toggle-insets" />
        </div>
        {cut.sources && <div data-testid="rough-cut-sources" style={{ marginTop: 14, fontSize: 12, color: "var(--ink-60)", lineHeight: 1.6 }}>Sources: {cut.sources.join(" → ")}</div>}
        {cut.status?.startsWith("error") && <div data-testid="rough-cut-error" style={{ marginTop: 10, fontSize: 12, color: "var(--burgundy)" }}>{cut.status}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: "auto", paddingTop: 18 }}>
          <button className={`btn ${cut.url ? "btn-ghost" : "btn-primary"}`} data-testid="build-rough-cut" disabled={busy} onClick={() => onBuild({ titles, insets })}>
            {cut.url ? <RefreshCw size={14} /> : <Scissors size={14} />}{cut.url ? "Rebuild rough cut" : "Build rough cut"}
          </button>
          {cut.url && <a className="btn btn-ghost" data-testid="download-rough-cut" href={`${BACKEND_URL}${cut.url}`} download="marova_rough_cut.mp4" target="_blank" rel="noreferrer"><Download size={14} />MP4</a>}
        </div>
      </div>
    </div>
  );
}
